"use client";

import { useEffect, useRef, useState } from "react";

export type MediaSource = "camera" | "file";

export type KycMediaValue = {
  file: File;
  source: MediaSource;
  previewUrl: string;
  capturedAt: number;
} | null;

type KycMediaFieldProps = {
  label: string;
  hint: string;
  accept?: string;
  allowPdf?: boolean;
  requireCamera?: boolean;
  required?: boolean;
  value: KycMediaValue;
  onChange: (value: KycMediaValue) => void;
};

function inferType(file: File, allowPdf: boolean) {
  if (file.type) return file.type;
  const ext = file.name.split(".").pop()?.toLowerCase() || "";
  if (["jpg", "jpeg", "jfif"].includes(ext)) return "image/jpeg";
  if (ext === "png") return "image/png";
  if (ext === "webp") return "image/webp";
  if (ext === "pdf" && allowPdf) return "application/pdf";
  return "";
}

function asTypedFile(file: File, allowPdf: boolean) {
  const type = inferType(file, allowPdf);
  if (!type || type === file.type) return file;
  return new File([file], file.name, { type });
}

export default function KycMediaField({
  label,
  hint,
  accept = "image/jpeg,image/png,image/webp,image/*",
  allowPdf = false,
  requireCamera = false,
  required = false,
  value,
  onChange,
}: KycMediaFieldProps) {
  const fileRef = useRef<HTMLInputElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [cameraError, setCameraError] = useState("");

  const stopCamera = () => {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    setCameraOpen(false);
  };

  useEffect(() => () => stopCamera(), []);

  useEffect(() => {
    if (!cameraOpen || !videoRef.current || !streamRef.current) return;
    videoRef.current.srcObject = streamRef.current;
    void videoRef.current.play().catch(() => undefined);
  }, [cameraOpen]);

  const openCamera = async () => {
    setCameraError("");
    if (!navigator.mediaDevices?.getUserMedia) {
      setCameraError("Camera not available on this device — use Attach instead");
      return;
    }
    try {
      stopCamera();
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: requireCamera ? "user" : { ideal: "environment" }, width: { ideal: 1280 }, height: { ideal: 720 } },
        audio: false,
      });
      streamRef.current = stream;
      setCameraOpen(true);
    } catch {
      setCameraError("Camera permission denied — allow camera or use Attach");
    }
  };

  const capturePhoto = () => {
    const video = videoRef.current;
    if (!video) return;
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth || 960;
    canvas.height = video.videoHeight || 720;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    canvas.toBlob((blob) => {
      if (!blob) {
        setCameraError("Could not capture photo — try again or attach a file");
        return;
      }
      const file = new File([blob], `capture-${Date.now()}.jpg`, { type: "image/jpeg" });
      if (value?.previewUrl) URL.revokeObjectURL(value.previewUrl);
      onChange({
        file,
        source: "camera",
        previewUrl: URL.createObjectURL(blob),
        capturedAt: Date.now(),
      });
      stopCamera();
    }, "image/jpeg", 0.92);
  };

  const onFilePicked = (file: File | null) => {
    if (!file) return;
    const typed = asTypedFile(file, allowPdf);
    const okImage = ["image/jpeg", "image/png", "image/webp"].includes(typed.type);
    const okPdf = allowPdf && typed.type === "application/pdf";
    if (!okImage && !okPdf) {
      setCameraError("Use JPG, PNG, WebP" + (allowPdf ? " or PDF" : "") + ". HEIC photos: Capture photo instead.");
      return;
    }
    if (typed.size <= 0 || typed.size > 8 * 1024 * 1024) {
      setCameraError("File must be under 8 MB");
      return;
    }
    setCameraError("");
    if (value?.previewUrl) URL.revokeObjectURL(value.previewUrl);
    onChange({
      file: typed,
      source: "file",
      previewUrl: okImage ? URL.createObjectURL(typed) : "",
      capturedAt: Date.now(),
    });
  };

  const clear = () => {
    if (value?.previewUrl) URL.revokeObjectURL(value.previewUrl);
    onChange(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  const missing = required && !value;

  return (
    <div className={missing ? "kyc-media missing" : "kyc-media"} id={label.replace(/\W+/g, "-").toLowerCase()}>
      <div className="kyc-media-head">
        <b>{label}{required ? " *" : ""}</b>
        <small>{hint}{requireCamera ? " · Live selfie must use Capture photo" : ""}</small>
      </div>

      <div className="kyc-media-actions">
        <button type="button" className="media-btn" onClick={() => fileRef.current?.click()}>Attach file</button>
        <button type="button" className="media-btn capture" onClick={() => void openCamera()}>Capture photo</button>
        {value && <button type="button" className="media-btn clear" onClick={clear}>Remove</button>}
      </div>

      <input
        ref={fileRef}
        type="file"
        accept={allowPdf ? `${accept},application/pdf,.pdf,.jpg,.jpeg,.png,.webp` : `${accept},.jpg,.jpeg,.png,.webp`}
        hidden
        onChange={(e) => onFilePicked(e.target.files?.[0] || null)}
      />

      {missing && <p className="kyc-media-error">Required — attach a file or capture a photo</p>}
      {cameraError && <p className="kyc-media-error">{cameraError}</p>}

      {cameraOpen && (
        <div className="kyc-camera">
          <video ref={videoRef} playsInline muted autoPlay />
          <div className="kyc-camera-actions">
            <button type="button" className="primary" onClick={capturePhoto}>Take photo</button>
            <button type="button" className="media-btn clear" onClick={stopCamera}>Cancel</button>
          </div>
        </div>
      )}

      {value && (
        <div className="kyc-media-preview">
          {value.previewUrl
            ? <img src={value.previewUrl} alt={`${label} preview`} />
            : <span className="pdf-chip">PDF attached · {value.file.name}</span>}
          <small>{value.source === "camera" ? "Captured from camera" : "Attached from device"} · {value.file.name} · {(value.file.size / 1024).toFixed(0)} KB</small>
        </div>
      )}
    </div>
  );
}
